import styled from 'styled-components';

import { BUTTON_SIZES, PrimaryButton } from 'ui/button';
import { ClickToCopyCode } from 'ui/clickToCopy';
import { Link } from 'ui/links';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { Table } from 'ui/table';
import { TableCell, TableCellProps } from 'ui/tableCell';
import { TableHeader } from 'ui/tableHeader';
import { HeaderCell } from 'ui/tableHeaderCell';
import { TableRows, TableRowsColumn } from 'ui/tableRows';
import { body2, body2Medium, spacing } from 'ui/theme';
import noop from 'utils/noop';

import { AlertsModalType, useAlertsModalContext } from '../../context';
import {
  DISPLAY_WEBHOOK_JSON_ATTRIBUTES_BUTTON_TEST_ID,
  DISPLAY_WEBHOOK_JSON_ATTRIBUTES_DOCS_LINK_TEST_ID,
  DISPLAY_WEBHOOK_JSON_ATTRIBUTES_MODAL_TEST_ID,
  JSON_ATTRIBUTE_HEADER_COLUMNS,
  JSON_ATTRIBUTE_ROWS,
  WebhookJSONKey,
} from './DisplayWebhookJSONAttributes.constants';

const StyledModalBody = styled(ModalBody)`
  padding: 0;
`;

const StyledTableHeader = styled(TableHeader)`
  ${HeaderCell} {
    ${body2Medium}
    padding: ${({ theme }) => `${theme.spacing.values.threeQuarter} ${theme.spacing.values.base}`};
  }

  ${HeaderCell}:nth-child(1) {
    padding: ${({ theme }) =>
      `${theme.spacing.values.threeQuarter} ${theme.spacing.values.base} ${theme.spacing.values.threeQuarter} ${theme.spacing.values.double}`};
  }
`;

export const Cell = styled(TableCell)<{ $secondary?: boolean }>`
  ${body2}
  text-align: left;
  vertical-align: middle;
  color: ${({ theme, $secondary }) =>
    $secondary ? theme.colors.textColors.textSecondary : theme.colors.textColors.textPrimary};
  padding: ${({ theme }) => `${theme.spacing.values.threeQuarter} ${theme.spacing.values.base}`};
`;

export const StyledTableRows = styled(TableRows)`
  & ${Cell}:nth-child(1) {
    padding: ${({ theme }) =>
      `${theme.spacing.values.threeQuarter} ${theme.spacing.values.base} ${theme.spacing.values.threeQuarter} ${theme.spacing.values.double}`};
  }
`;

const ActionsWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.base};
`;

type CellProps = TableCellProps & {
  row: {
    id: WebhookJSONKey;
    jsonKey: WebhookJSONKey;
    description: string;
    example: string;
  };
};

export const JSON_ATTRIBUTE_BODY_COLUMNS: TableRowsColumn[] = [
  {
    RowCell: (props: CellProps) => (
      <Cell {...props}>
        <ClickToCopyCode isCopyable content={props?.row?.jsonKey} />
      </Cell>
    ),
    align: 'flex-start',
    name: 'JSON Key',
    value: 'jsonKey',
    sortable: false,
  },
  {
    RowCell: (props: CellProps) => <Cell {...props}>{props?.row?.description}</Cell>,
    align: 'flex-start',
    name: 'Description',
    value: 'description',
    sortable: false,
  },
  {
    RowCell: (props: CellProps) => (
      <Cell {...props} $secondary>
        {props?.row?.example}
      </Cell>
    ),
    align: 'flex-start',
    name: 'Example',
    value: 'example',
    sortable: false,
  },
];

export const DisplayWebhookJSONAttributes = () => {
  const { closeModal } = useAlertsModalContext();

  return (
    <Modal
      withPortal
      isModalOpen={true}
      onClose={() => closeModal(AlertsModalType.DisplayWebhookJSONAttributes)}
      id={DISPLAY_WEBHOOK_JSON_ATTRIBUTES_MODAL_TEST_ID}
      data-test={DISPLAY_WEBHOOK_JSON_ATTRIBUTES_MODAL_TEST_ID}
      size={MODAL_SIZES.LARGE}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader>JSON Attributes</ModalHeader>

      <StyledModalBody>
        <Table>
          <StyledTableHeader
            columns={JSON_ATTRIBUTE_HEADER_COLUMNS}
            setSortDirection={noop}
            setSortedBy={noop}
            sortDirection="desc"
            sortedBy="name"
            isSticky
          />
          <StyledTableRows columns={JSON_ATTRIBUTE_BODY_COLUMNS} rows={JSON_ATTRIBUTE_ROWS} />
        </Table>
      </StyledModalBody>

      <ModalFooter hasCancel={false}>
        <ActionsWrapper>
          <Link
            target="_blank"
            data-test={DISPLAY_WEBHOOK_JSON_ATTRIBUTES_DOCS_LINK_TEST_ID}
            href="https://support.iterable.com/hc/en-us/articles/19437186634772-Campaign-Performance-Alerts-#create-a-slack-workflow"
            hasOpenNewTabIcon
          >
            Setting up a Slack Automation
          </Link>

          <PrimaryButton
            data-test={DISPLAY_WEBHOOK_JSON_ATTRIBUTES_BUTTON_TEST_ID}
            onClick={() => closeModal(AlertsModalType.DisplayWebhookJSONAttributes)}
            size={BUTTON_SIZES.LARGE}
          >
            Done
          </PrimaryButton>
        </ActionsWrapper>
      </ModalFooter>
    </Modal>
  );
};
